import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  NotificationsOff as NotificationsOffIcon,
} from '@mui/icons-material';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';

const NotificationsScreen = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [markingReadId, setMarkingReadId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadData(1);
  }, []);

  const loadData = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `api/school-common/notification/get-notification?PageNumber=${page}&PageSize=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const newItems = response.data.data || [];

      if (page === 1) {
        setItems(newItems);
      } else {
        setItems((prevItems) => [...prevItems, ...newItems]);
      }

      setPageNumber(page + 1);
      setHasMoreData(newItems.length === 10);
    } catch (error) {
      console.error('NotificationsScreen loadData error:', error);
      handleCatch(error);
      if (page === 1) setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (id) => {
    setDeleteItemId(id);
    setDeleteDialogOpen(true);
  };

  const deleteItem = async () => {
    if (!deleteItemId) return;

    try {
      setDeletingId(deleteItemId);
      setDeleteDialogOpen(false);
      const token = localStorage.getItem('userToken');

      await api.delete(`api/school-common/notification?id=${deleteItemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setItems((prevItems) => prevItems.filter((item) => item.id !== deleteItemId));
      setNotification({ open: true, message: 'Notification deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('NotificationsScreen deleteItem error:', error);
      handleCatch(error);
      setNotification({ open: true, message: 'Failed to delete notification', severity: 'error' });
    } finally {
      setDeletingId(null);
      setDeleteItemId(null);
    }
  };

  const onRefresh = useCallback(async () => {
    setPageNumber(1);
    setHasMoreData(true);
    await loadData(1);
  }, []);

  const loadMore = () => {
    if (!loading && hasMoreData) {
      loadData(pageNumber);
    }
  };

  const updateUnread = async (id) => {
    try {
      setMarkingReadId(id);
      const token = localStorage.getItem('userToken');
      const data = { id: id };

      await api.put('api/school-common/notification/update-unread', JSON.stringify(data), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      setNotification({ open: true, message: 'Marked as read', severity: 'success' });

      // Update local state
      setItems((prevItems) =>
        prevItems.map((item) => (item.id === id ? { ...item, isRead: true } : item))
      );
    } catch (error) {
      console.error('NotificationsScreen UpdateUnread error:', error);
      handleCatch(error);
      setNotification({ open: true, message: 'Failed to mark as read', severity: 'error' });
    } finally {
      setMarkingReadId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader
        title="Notifications"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box sx={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', backgroundColor: '#F5F7FA' }}>
        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pt: 8, px: 3 }}>
            <NotificationsOffIcon sx={{ fontSize: 64, color: '#E0E0E0', mb: 2 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#333', mb: 1 }}>All Clear! 🎉</Typography>
            <Typography sx={{ fontSize: 13, color: '#888', textAlign: 'center', mb: 3 }}>No new notifications. You're all caught up!</Typography>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefresh} sx={{ borderRadius: 20 }}>
              Check for Updates
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ backgroundColor: '#fff', mt: 1, mx: 1, borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              {items.map((item, index) => (
                <React.Fragment key={item.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      px: 2,
                      py: 1.5,
                      bgcolor: item.isRead ? '#fff' : '#FFF8E1',
                      cursor: item.isRead ? 'default' : 'pointer',
                    }}
                    onClick={() => !item.isRead && markingReadId !== item.id && updateUnread(item.id)}
                  >
                    {/* Icon */}
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '50%',
                        bgcolor: item.isRead ? '#F5F5F5' : '#FFF3E0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        mr: 1.5,
                      }}
                    >
                      {markingReadId === item.id ? (
                        <CircularProgress size={18} sx={{ color: '#FF9800' }} />
                      ) : item.isRead ? (
                        <NotificationsIcon sx={{ fontSize: 18, color: '#9E9E9E' }} />
                      ) : (
                        <NotificationsActiveIcon sx={{ fontSize: 18, color: '#FF9800' }} />
                      )}
                    </Box>

                    {/* Text */}
                    <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                      <Typography sx={{ fontWeight: item.isRead ? 500 : 700, fontSize: 14, color: '#222', lineHeight: 1.3, mb: 0.25 }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#666', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>
                        {item.description}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#aaa', mt: 0.5 }}>
                        {formatDate(item.createdDate)}
                      </Typography>
                    </Box>

                    {/* Delete */}
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); confirmDelete(item.id); }}
                      disabled={deletingId === item.id}
                      sx={{ color: '#ccc', p: 0.5, flexShrink: 0 }}
                    >
                      {deletingId === item.id
                        ? <CircularProgress size={14} sx={{ color: '#ccc' }} />
                        : <DeleteIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </Box>
                  {index < items.length - 1 && <Divider sx={{ ml: '68px' }} />}
                </React.Fragment>
              ))}
            </Box>

            {hasMoreData && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Button variant="outlined" onClick={loadMore} disabled={loading} sx={{ borderRadius: 20, minWidth: 160 }}>
                  {loading ? <CircularProgress size={20} /> : 'Load More'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this notification?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteItem} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} variant="filled" sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationsScreen;
